// NewPlaceAutocomplete.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, Navigation, Loader2, X, LocateFixed } from 'lucide-react';

const NewPlaceAutocomplete = ({ onLocationSelect, apiKey }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const geocoder = useRef(null);

  // Load Google Maps API
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsGoogleLoaded(true);
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
        geocoder.current = new window.google.maps.Geocoder();
      };
      document.head.appendChild(script);
    } else {
      setIsGoogleLoaded(true);
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
      geocoder.current = new window.google.maps.Geocoder();
    }
  }, [apiKey]);

  // Get current location with detailed address
  const getCurrentLocation = () => {
    setIsLoading(true);
    setError('');

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Use Google Geocoding API to get detailed address
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
          );

          if (!response.ok) throw new Error('Failed to fetch location data');

          const data = await response.json();

          if (data.status === 'OK' && data.results.length > 0) {
            const address = data.results[0];
            const addressComponents = address.address_components;
            const formattedAddress = address.formatted_address;

            // Extract address components
            const extractComponent = (type) => {
              const component = addressComponents.find(comp => comp.types.includes(type));
              return component ? component.long_name : '';
            };

            const locationData = {
              latitude,
              longitude,
              formattedAddress,
              street: extractComponent('route'),
              streetNumber: extractComponent('street_number'),
              locality: extractComponent('locality'),
              sublocality: extractComponent('sublocality'),
              city: extractComponent('locality') || extractComponent('administrative_area_level_2'),
              state: extractComponent('administrative_area_level_1'),
              country: extractComponent('country'),
              postalCode: extractComponent('postal_code'),
              neighborhood: extractComponent('neighborhood'),
              // E-commerce friendly address format
              displayAddress: formattedAddress,
              shortAddress: `${extractComponent('locality')}, ${extractComponent('administrative_area_level_1')}`,
              area: extractComponent('sublocality') || extractComponent('neighborhood') || extractComponent('locality'),
              placeId: address.place_id, // Added placeId from geocoding result
              locationType: 'current_location'
            };

            setCurrentLocation(locationData);
            setSearchQuery(locationData.displayAddress);
            onLocationSelect?.(locationData);
          } else {
            throw new Error('No address found for this location');
          }
        } catch (err) {
          setError('Failed to get location details: ' + err.message);
          console.error('Geocoding error:', err);
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        let errorMessage = 'Failed to get current location';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please check your connection.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = 'An unknown error occurred while getting location.';
            break;
        }

        setError(errorMessage);
        setIsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000
      }
    );
  };

  // Handle search input with Google Places Autocomplete
  // const handleSearchChange = (e) => {
  //   const value = e.target.value;
  //   setSearchQuery(value);

  //   if (!value.trim() || !isGoogleLoaded) {
  //     setPredictions([]);
  //     return;
  //   }

  //   if (autocompleteService.current) {
  //     // Use only 'geocode' type which includes addresses and places
  //     autocompleteService.current.getPlacePredictions(
  //       {
  //         input: value,
  //         // types: ['geocode'], // Only use one type - 'geocode' includes addresses and places
  //         componentRestrictions: { country: 'IN' } // Change country code as needed
  //       },
  //       (predictions, status) => {
  //         if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
  //           setPredictions(predictions);
  //         } else {
  //           setPredictions([]);
  //         }
  //       }
  //     );
  //   }
  // };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (!value.trim() || !isGoogleLoaded) {
      setPredictions([]);
      return;
    }

    if (autocompleteService.current) {
      // Allow all types of places (cities, postal codes, landmarks, etc.)
      autocompleteService.current.getPlacePredictions(
        {
          input: value,
          // Removed "types" restriction to allow full search (city, state, country, pincode, lat-long, etc.)
          // Use region + country restriction for better accuracy
          componentRestrictions: { country: 'IN' },
          // Add location bias (optional, helps prioritize nearby results)
          // locationBias: { lat: 20.5937, lng: 78.9629 } // India's center
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(predictions);
          } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            // As fallback, if no predictions, try manual geocoding (lat/long or postal code)
            handleManualSearch(value);
          } else {
            setPredictions([]);
          }
        }
      );
    }
  };

  const handleManualSearch = async (query) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${apiKey}`
      );

      const data = await response.json();
      if (data.status === 'OK' && data.results.length > 0) {
        const results = data.results.map(result => ({
          description: result.formatted_address,
          place_id: result.place_id,
          structured_formatting: {
            main_text: result.address_components[0]?.long_name || result.formatted_address,
            secondary_text: result.formatted_address
          }
        }));
        setPredictions(results);
      } else {
        setPredictions([]);
      }
    } catch (error) {
      console.error('Manual search failed:', error);
      setPredictions([]);
    }
  };

  // Get place details when a prediction is selected
  const handlePredictionSelect = (prediction) => {
    setIsLoading(true);
    setSearchQuery(prediction.description);
    setPredictions([]);

    // Use Geocoding API instead of Places API for more consistent address results
    if (geocoder.current) {
      geocoder.current.geocode(
        { placeId: prediction.place_id },
        (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results && results[0]) {
            const place = results[0];
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            // Extract address components
            const addressComponents = place.address_components || [];
            const extractComponent = (type) => {
              const component = addressComponents.find(comp => comp.types.includes(type));
              return component ? component.long_name : '';
            };

            const locationData = {
              latitude: lat,
              longitude: lng,
              formattedAddress: place.formatted_address,
              street: extractComponent('route'),
              streetNumber: extractComponent('street_number'),
              locality: extractComponent('locality'),
              sublocality: extractComponent('sublocality'),
              city: extractComponent('locality') || extractComponent('administrative_area_level_2'),
              state: extractComponent('administrative_area_level_1'),
              country: extractComponent('country'),
              postalCode: extractComponent('postal_code'),
              neighborhood: extractComponent('neighborhood'),
              displayAddress: place.formatted_address,
              shortAddress: `${extractComponent('locality')}, ${extractComponent('administrative_area_level_1')}`,
              area: extractComponent('sublocality') || extractComponent('neighborhood') || extractComponent('locality'),
              placeId: prediction.place_id, // Added placeId from prediction
              locationType: 'searched_address'
            };

            setCurrentLocation(locationData);
            onLocationSelect?.(locationData);
          } else {
            // Fallback: try using the prediction directly
            handleFallbackGeocode(prediction);
          }
          setIsLoading(false);
        }
      );
    }
  };

  // Fallback geocoding using the prediction description
  const handleFallbackGeocode = async (prediction) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(prediction.description)}&key=${apiKey}`
      );

      if (!response.ok) throw new Error('Geocoding failed');

      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const place = data.results[0];
        const lat = place.geometry.location.lat;
        const lng = place.geometry.location.lng;

        const addressComponents = place.address_components || [];
        const extractComponent = (type) => {
          const component = addressComponents.find(comp => comp.types.includes(type));
          return component ? component.long_name : '';
        };

        const locationData = {
          latitude: lat,
          longitude: lng,
          formattedAddress: place.formatted_address,
          street: extractComponent('route'),
          streetNumber: extractComponent('street_number'),
          locality: extractComponent('locality'),
          sublocality: extractComponent('sublocality'),
          city: extractComponent('locality') || extractComponent('administrative_area_level_2'),
          state: extractComponent('administrative_area_level_1'),
          country: extractComponent('country'),
          postalCode: extractComponent('postal_code'),
          neighborhood: extractComponent('neighborhood'),
          displayAddress: place.formatted_address,
          shortAddress: `${extractComponent('locality')}, ${extractComponent('administrative_area_level_1')}`,
          area: extractComponent('sublocality') || extractComponent('neighborhood') || extractComponent('locality'),
          placeId: prediction.place_id, // Added placeId from prediction
          locationType: 'searched_address_fallback'
        };

        setCurrentLocation(locationData);
        onLocationSelect?.(locationData);
      } else {
        setError('Could not find details for this location');
      }
    } catch (err) {
      setError('Failed to get location details');
      console.error('Fallback geocoding error:', err);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setPredictions([]);
    setCurrentLocation(null);
    setError('');
  };

  return (
    <div className="location-picker w-full max-w-2xl mx-auto">
      {/* Search Input with Detect Location */}
      <div className="relative mb-1">
        <div className="relative flex items-center">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Enter your delivery address, area, or landmark..."
            className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
            disabled={!isGoogleLoaded}
          />

          {/* Detect Current Location Button (moved to right) */}
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={isLoading || !isGoogleLoaded}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors font-medium"
          >
            <LocateFixed className="h-3.5 w-3.5" />
            Detect
          </button>

          {searchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-24 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {isLoading && (
            <Loader2 className="absolute right-28 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 animate-spin" />
          )}
        </div>

        {!isGoogleLoaded && (
          <p className="text-xs text-gray-500 mt-2">Loading location services...</p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Current Location Display */}
      {currentLocation && predictions.length === 0 && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-800 mb-2">
            <MapPin className="h-4 w-4" />
            <span className="font-semibold">Delivery Location</span>
          </div>
          <p className="text-green-700 font-medium">{currentLocation.displayAddress}</p>
          {currentLocation.area && (
            <p className="text-sm text-green-600 mt-1">Area: {currentLocation.area}</p>
          )}
          <button
            type="button"
            onClick={clearSearch}
            className="mt-2 text-sm text-green-600 hover:text-green-800 underline"
          >
            Change location
          </button>
        </div>
      )}

      {/* Autocomplete Predictions */}
      {predictions.length > 0 && (
        <div className="search-results border border-gray-200 rounded-lg overflow-hidden shadow-lg max-h-60 overflow-y-auto">
          {predictions.map((prediction) => (
            <button
              type="button"
              key={prediction.place_id}
              onClick={() => handlePredictionSelect(prediction)}
              className="w-full text-left p-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {prediction.structured_formatting.main_text}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {prediction.structured_formatting.secondary_text}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>

  );
};

export default NewPlaceAutocomplete;